import React, { createContext, useContext, useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { io } from 'socket.io-client';
import { useAuth } from './AuthContext';

// Undefined (not '') when unset on purpose: socket.io only falls back to the page
// origin for a null/undefined URL. An empty string is still a string to its parser,
// which would build the host-less URL "http://" and fail to connect. Leaving this
// unset is the normal case — the socket is same-origin behind the dev-server proxy.
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || undefined;
const SOCKET_PATH = '/api/socket.io';

// A snapshot ack must come back inside this window. A connected-but-silent socket
// (the classic mobile "zombie" left behind when the OS suspended the tab) never
// answers, and socket.io only notices when its own ping timeout expires tens of
// seconds later. The ack timeout is what lets us tear the transport down early.
const PRESENCE_SNAPSHOT_ACK_TIMEOUT_MS = 5000;
// Never force two reconnects inside this window, so a genuinely slow network can
// not be turned into a reconnect loop.
const ZOMBIE_RECOVERY_COOLDOWN_MS = 10000;
// Safety net: presence can not drift for longer than this while the tab is in
// front of the user, even if every edge event was missed.
const PRESENCE_POLL_INTERVAL_MS = 45000;
// One shared clock refreshes every "last seen" label without a timer per row.
const PRESENCE_CLOCK_INTERVAL_MS = 60000;

// The provider publishes five separate contexts instead of one bundle. Presence
// pings, a peer's keystroke and the unread badge each used to invalidate a single
// combined value, which re-rendered every consumer (and through them every message
// bubble). Splitting them means a component only re-renders for the slice it reads.
const SocketActionsContext = createContext(null);
const SocketStatusContext = createContext(null);
const SocketPresenceContext = createContext(null);
const SocketTypingContext = createContext(null);
const SocketUnreadContext = createContext(null);

const useRequiredContext = (context, hookName) => {
  const value = useContext(context);
  if (!value) {
    throw new Error(`${hookName} must be used within a SocketProvider`);
  }
  return value;
};

// Stable for the provider's lifetime — safe to read from anywhere without cost.
export const useSocketActions = () => useRequiredContext(SocketActionsContext, 'useSocketActions');
export const useSocketStatus = () => useRequiredContext(SocketStatusContext, 'useSocketStatus');
export const useSocketPresence = () => useRequiredContext(SocketPresenceContext, 'useSocketPresence');
export const useSocketTyping = () => useRequiredContext(SocketTypingContext, 'useSocketTyping');
export const useSocketUnread = () => useRequiredContext(SocketUnreadContext, 'useSocketUnread');

// Back-compat: reads every slice, so it re-renders on any socket change. Prefer the
// narrow hooks above in anything that renders a list.
export const useSocket = () => {
  const actions = useSocketActions();
  const status = useSocketStatus();
  const presence = useSocketPresence();
  const typing = useSocketTyping();
  const unread = useSocketUnread();
  return useMemo(
    () => ({ ...actions, ...status, ...presence, ...typing, ...unread }),
    [actions, status, presence, typing, unread]
  );
};

export const SocketProvider = ({ children }) => {
  const { user, getSocketToken } = useAuth();
  const socketRef = useRef(null);
  const [connected, setConnected] = useState(false);
  const [authenticated, setAuthenticated] = useState(false);
  const [networkOnline, setNetworkOnline] = useState(() => navigator.onLine !== false);
  const [typingUsers, setTypingUsers] = useState({});
  const [dmTypingUsers, setDmTypingUsers] = useState({});
  const [presenceByUser, setPresenceByUser] = useState({});
  const [presenceNow, setPresenceNow] = useState(() => Date.now());
  const [totalUnreadCount, setTotalUnreadCount] = useState(0);

  const reconnectAttempts = useRef(0);
  const joinedChannelsRef = useRef(new Set());
  const joinedDmsRef = useRef(new Set());
  const typingTimeoutsRef = useRef({});
  const zombieRecoveryAtRef = useRef(0);
  const currentUserIdRef = useRef(null);
  // join_channel / join_dm must not be re-created when the auth flag flips, or the
  // actions context would change identity and re-render every consumer.
  const authenticatedRef = useRef(false);
  useEffect(() => { authenticatedRef.current = authenticated; }, [authenticated]);
  useEffect(() => { currentUserIdRef.current = user?.id ? String(user.id) : null; }, [user?.id]);
  // Keep component subscriptions independent of the socket object's creation time.
  // A subscriber may mount while the auth/token request is still in flight; when the
  // socket is created (or recreated for another authenticated session), all currently
  // registered callbacks are attached exactly once.
  const eventSubscriptionsRef = useRef(new Map());

  useEffect(() => {
    const offline = () => setNetworkOnline(false);
    const online = () => setNetworkOnline(true);
    window.addEventListener('offline', offline);
    window.addEventListener('online', online);
    return () => { window.removeEventListener('offline', offline); window.removeEventListener('online', online); };
  }, []);

  const subscribeToSocketEvent = useCallback((eventName, callback) => {
    if (!eventName || typeof callback !== 'function') return () => {};

    let callbacks = eventSubscriptionsRef.current.get(eventName);
    if (!callbacks) {
      callbacks = new Set();
      eventSubscriptionsRef.current.set(eventName, callbacks);
    }
    if (callbacks.has(callback)) return () => {};
    callbacks.add(callback);
    socketRef.current?.on(eventName, callback);

    return () => {
      callbacks.delete(callback);
      socketRef.current?.off(eventName, callback);
      if (callbacks.size === 0) eventSubscriptionsRef.current.delete(eventName);
    };
  }, []);

  const mergePresenceEntries = useCallback((entries, options = {}) => {
    const list = Array.isArray(entries) ? entries : [entries];
    const receivedAt = Date.now();
    const { snapshotRequestedAt = null } = options;

    setPresenceByUser((previousMap) => {
      let changed = false;
      const nextMap = { ...previousMap };

      list.filter(Boolean).forEach((entry) => {
        if (entry.user_id == null) return;
        const userId = String(entry.user_id);
        const previous = previousMap[userId];
        // Do not let a slower snapshot overwrite a realtime event received after
        // that snapshot request began.
        if (
          snapshotRequestedAt != null
          && previous?.updated_at
          && previous.updated_at >= snapshotRequestedAt
        ) {
          return;
        }

        const updated = {
          ...(previous || {}),
          user_id: userId,
          updated_at: receivedAt,
        };
        if (Object.prototype.hasOwnProperty.call(entry, 'is_online')) {
          updated.is_online = Boolean(entry.is_online);
        }
        if (entry.last_seen != null) updated.last_seen = entry.last_seen;

        // Nothing observable changed: keep the previous object so consumers that
        // memoize on it are not invalidated by a no-op presence ping.
        const isNoop = previous
          && previous.is_online === updated.is_online
          && previous.last_seen === updated.last_seen;
        if (isNoop) return;

        nextMap[userId] = updated;
        changed = true;
      });

      return changed ? nextMap : previousMap;
    });
  }, []);

  const ensureConnection = useCallback(() => {
    const socket = socketRef.current;
    if (socket && !socket.connected) {
      // The `connect` handler re-authenticates and requests a fresh snapshot, so
      // reopening the transport is all that is needed here.
      try { socket.connect(); } catch { /* socket.io throws if already opening */ }
    }
  }, []);

  // Connected-but-silent transport. Android Chrome hands back a socket in this state
  // after the app was in the background: `connected` is true, nothing flows, and no
  // message or presence event arrives until something forces a real reconnect.
  const recoverZombieSocket = useCallback(() => {
    const socket = socketRef.current;
    if (!socket) return;
    const now = Date.now();
    if (now - zombieRecoveryAtRef.current < ZOMBIE_RECOVERY_COOLDOWN_MS) return;
    zombieRecoveryAtRef.current = now;
    console.warn('Socket stopped answering; forcing reconnect');
    try { socket.disconnect(); } catch { /* already closed */ }
    try { socket.connect(); } catch { /* will retry on the next revive */ }
  }, []);

  const requestPresenceSnapshot = useCallback(() => {
    const socket = socketRef.current;
    if (!socket) return;
    if (!socket.connected) {
      ensureConnection();
      return;
    }

    const snapshotRequestedAt = Date.now();
    let settled = false;
    const watchdog = setTimeout(() => {
      if (settled) return;
      settled = true;
      recoverZombieSocket();
    }, PRESENCE_SNAPSHOT_ACK_TIMEOUT_MS);

    socket.emit('get_presence_snapshot', {}, (response) => {
      if (settled) return;
      settled = true;
      clearTimeout(watchdog);
      if (response?.status === 'ok') {
        mergePresenceEntries(response.presence || [], { snapshotRequestedAt });
      }
    });
  }, [ensureConnection, mergePresenceEntries, recoverZombieSocket]);

  const requestPresenceSnapshotRef = useRef(requestPresenceSnapshot);
  useEffect(() => { requestPresenceSnapshotRef.current = requestPresenceSnapshot; }, [requestPresenceSnapshot]);

  // Any inbound traffic from a peer proves they are online right now. Edge events
  // can be missed (suspended mobile transport, or a server-side online flag that
  // never flipped because the peer's other device kept it set), which is what left
  // DM headers showing "last seen 2 hours ago" for someone whose message had just
  // arrived. Deriving presence from traffic we can actually see closes that gap
  // without waiting for the next snapshot.
  const notePeerActivity = useCallback((userId) => {
    if (userId == null) return;
    const normalizedId = String(userId);
    if (!normalizedId || normalizedId === currentUserIdRef.current) return;
    mergePresenceEntries({ user_id: normalizedId, is_online: true });
  }, [mergePresenceEntries]);

  const onlineUsers = useMemo(() => new Set(
    Object.entries(presenceByUser)
      .filter(([, presence]) => presence?.is_online)
      .map(([userId]) => userId)
  ), [presenceByUser]);

  const logBootTiming = useCallback((event, extra = {}) => {
    try {
      if (localStorage.getItem('whisper_debug_boot_timing') !== 'true') return;
      console.info('[whisper boot]', event, {
        t: Math.round(performance.now()),
        ...extra,
      });
    } catch {
      // Debug logging should never affect socket startup.
    }
  }, []);

  // Update browser badge
  useEffect(() => {
    if (totalUnreadCount > 0) {
      document.title = `(${totalUnreadCount}) Whisper`;
      if ('setAppBadge' in navigator) {
        navigator.setAppBadge(totalUnreadCount).catch(() => {});
      }
    } else {
      document.title = 'Whisper';
      if ('clearAppBadge' in navigator) {
        navigator.clearAppBadge().catch(() => {});
      }
    }
  }, [totalUnreadCount]);

  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;
    let initRetryTimer = null;
    let initRetryCount = 0;

    const initSocket = async () => {
      try {
        const socketToken = await getSocketToken();
        if (cancelled) return;

        logBootTiming('socket-connect:start');
        socketRef.current = io(BACKEND_URL, {
          path: SOCKET_PATH,
          // Start with polling then upgrade — most reliable across mobile networks
          // and proxies. Keep reconnecting indefinitely so a phone that suspended
          // background networking recovers instead of giving up after a few tries.
          transports: ['polling', 'websocket'],
          upgrade: true,
          timeout: 20000,
          withCredentials: true,
          reconnection: true,
          reconnectionAttempts: Infinity,
          reconnectionDelay: 1000,
          reconnectionDelayMax: 5000,
        });

        eventSubscriptionsRef.current.forEach((callbacks, eventName) => {
          callbacks.forEach((callback) => socketRef.current?.on(eventName, callback));
        });

        socketRef.current.on('connect', () => {
          console.log('Socket connected');
          logBootTiming('socket-connect:success');
          setConnected(true);
          setAuthenticated(false);
          reconnectAttempts.current = 0;

          socketRef.current.emit('authenticate', { token: socketToken }, (response) => {
            if (response?.status === 'ok') {
              console.log('Socket authenticated');
              logBootTiming('socket-auth:success');
              setAuthenticated(true);
              mergePresenceEntries(response.presence || []);

              // Rejoin channels
              joinedChannelsRef.current.forEach((channelId) => {
                socketRef.current.emit('join_channel', { channel_id: channelId });
              });

              // Rejoin DMs
              joinedDmsRef.current.forEach((dmId) => {
                socketRef.current.emit('join_dm', { dm_id: dmId });
              });

              requestPresenceSnapshotRef.current?.();
            } else {
              console.error('Socket auth failed:', response);
            }
          });
        });

        socketRef.current.on('disconnect', (reason) => {
          console.log('Socket disconnected:', reason);
          setConnected(false);
          setAuthenticated(false);
          Object.values(typingTimeoutsRef.current).forEach(clearTimeout);
          typingTimeoutsRef.current = {};
          setTypingUsers({});
          setDmTypingUsers({});
          // Preserve the last snapshot during brief network interruptions. The
          // server grace period and next snapshot establish the final state.
        });

        socketRef.current.on('connect_error', (error) => {
          console.error('Socket connection error:', error);
          reconnectAttempts.current++;
        });

        // Online/Offline status. Coerce ids to String so the Set membership test
        // is stable regardless of how the id arrives. onlineUsers updates globally
        // for every presence event; the open-DM API reconciliation is triggered
        // narrowly in ChatPage (only when the event matches the open participant),
        // so we intentionally do NOT bump presenceRefreshTick here — that tick stays
        // reserved for reconnect / visibility / focus catch-up.
        socketRef.current.on('user_online', (data) => {
          mergePresenceEntries({ ...data, is_online: true });
        });

        socketRef.current.on('user_offline', (data) => {
          mergePresenceEntries({ ...data, is_online: false });
        });

        // Traffic-derived presence — see notePeerActivity.
        socketRef.current.on('new_dm_message', (data) => notePeerActivity(data?.sender_id));
        socketRef.current.on('new_message', (data) => notePeerActivity(data?.sender_id));
        socketRef.current.on('dm_messages_seen', (data) => notePeerActivity(data?.user_id));

        // Channel typing with auto-clear
        socketRef.current.on('typing_start', (data) => {
          notePeerActivity(data?.user_id);
          setTypingUsers((prev) => ({
            ...prev,
            [data.channel_id]: { user_id: data.user_id, username: data.username },
          }));

          const key = `channel_${data.channel_id}_${data.user_id}`;
          if (typingTimeoutsRef.current[key]) clearTimeout(typingTimeoutsRef.current[key]);
          typingTimeoutsRef.current[key] = setTimeout(() => {
            setTypingUsers((prev) => {
              const s = { ...prev };
              if (s[data.channel_id]?.user_id === data.user_id) delete s[data.channel_id];
              return s;
            });
          }, 3000);
        });

        socketRef.current.on('typing_stop', (data) => {
          const key = `channel_${data.channel_id}_${data.user_id}`;
          if (typingTimeoutsRef.current[key]) {
            clearTimeout(typingTimeoutsRef.current[key]);
            delete typingTimeoutsRef.current[key];
          }
          setTypingUsers((prev) => {
            const s = { ...prev };
            if (s[data.channel_id]?.user_id === data.user_id) delete s[data.channel_id];
            return s;
          });
        });

        // DM typing with auto-clear
        socketRef.current.on('dm_typing_start', (data) => {
          notePeerActivity(data?.user_id);
          setDmTypingUsers((prev) => ({
            ...prev,
            [data.dm_id]: { user_id: data.user_id, username: data.username },
          }));

          const key = `dm_${data.dm_id}_${data.user_id}`;
          if (typingTimeoutsRef.current[key]) clearTimeout(typingTimeoutsRef.current[key]);
          typingTimeoutsRef.current[key] = setTimeout(() => {
            setDmTypingUsers((prev) => {
              const s = { ...prev };
              if (s[data.dm_id]?.user_id === data.user_id) delete s[data.dm_id];
              return s;
            });
          }, 3000);
        });

        socketRef.current.on('dm_typing_stop', (data) => {
          const key = `dm_${data.dm_id}_${data.user_id}`;
          if (typingTimeoutsRef.current[key]) {
            clearTimeout(typingTimeoutsRef.current[key]);
            delete typingTimeoutsRef.current[key];
          }
          setDmTypingUsers((prev) => {
            const s = { ...prev };
            if (s[data.dm_id]?.user_id === data.user_id) delete s[data.dm_id];
            return s;
          });
        });
      } catch (error) {
        console.error('Failed to initialize socket:', error);
        if (!cancelled && ![401, 403].includes(error?.response?.status)) {
          // A failed token fetch previously left no socket to reconnect forever.
          // Bounded backoff repairs startup when a mobile connection returns.
          initRetryCount += 1;
          initRetryTimer = setTimeout(initSocket, Math.min(10000, 1000 * 2 ** Math.min(initRetryCount, 4)));
        }
      }
    };

    // Chrome prerenders what you type in the omnibox: typing "l" towards localhost
    // boots this whole app in a hidden renderer, socket included. Authenticating
    // from there makes the server mark the account online and flip every pending
    // DM to delivered — while the tab has not actually been opened, so the peer
    // sees "online" and double ticks for someone who is still away. Hold the
    // connection until the prerender is activated by a real navigation.
    let stopWaitingForActivation;
    if (document.prerendering) {
      const onActivation = () => {
        if (!cancelled) initSocket();
      };
      document.addEventListener('prerenderingchange', onActivation, { once: true });
      stopWaitingForActivation = () => {
        document.removeEventListener('prerenderingchange', onActivation);
      };
    } else {
      initSocket();
    }

    return () => {
      cancelled = true;
      clearTimeout(initRetryTimer);
      stopWaitingForActivation?.();
      Object.values(typingTimeoutsRef.current).forEach(clearTimeout);
      typingTimeoutsRef.current = {};
      joinedChannelsRef.current.clear();
      joinedDmsRef.current.clear();
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  }, [user?.id, getSocketToken, logBootTiming, mergePresenceEntries, notePeerActivity]);

  // One shared clock refreshes every last-seen label without one timer per row.
  useEffect(() => {
    const timer = setInterval(() => setPresenceNow(Date.now()), PRESENCE_CLOCK_INTERVAL_MS);
    return () => clearInterval(timer);
  }, []);

  // Recover mobile sockets after focus, visibility, bfcache, or network return and
  // reconcile a scoped snapshot.
  //
  // The previous version called socket.connect() and then checked socket.connected
  // in the SAME tick — connect() is asynchronous, so the flag was still false and
  // the catch-up snapshot was skipped in exactly the case that had just needed it.
  // Reconnects now rely on the `connect` handler (which re-authenticates and
  // snapshots), and an already-connected socket goes through the ack watchdog so a
  // silent transport is detected instead of being trusted.
  const reviveIfNeeded = useCallback(() => {
    setPresenceNow(Date.now());
    const socket = socketRef.current;
    if (!socket) return;
    if (!socket.connected) {
      ensureConnection();
      return;
    }
    requestPresenceSnapshot();
  }, [ensureConnection, requestPresenceSnapshot]);

  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        reviveIfNeeded();
      }
    };
    const handleFocus = () => reviveIfNeeded();

    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('focus', handleFocus);
    window.addEventListener('pageshow', handleFocus);
    window.addEventListener('online', handleFocus);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('pageshow', handleFocus);
      window.removeEventListener('online', handleFocus);
    };
  }, [reviveIfNeeded]);

  // Bounded staleness while the app is in front of the user. Cheap (one scoped
  // query for the caller's DM contacts) and it doubles as a liveness probe, since
  // the ack watchdog turns a missing reply into a reconnect.
  useEffect(() => {
    const timer = setInterval(() => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      requestPresenceSnapshot();
    }, PRESENCE_POLL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [requestPresenceSnapshot]);

  // Channel functions
  const joinChannel = useCallback((channelId) => {
    if (!channelId) return;
    joinedChannelsRef.current.add(channelId);
    if (socketRef.current?.connected && authenticatedRef.current) {
      socketRef.current.emit('join_channel', { channel_id: channelId });
    }
  }, []);

  const leaveChannel = useCallback((channelId) => {
    if (!channelId) return;
    joinedChannelsRef.current.delete(channelId);
    if (socketRef.current?.connected) {
      socketRef.current.emit('leave_channel', { channel_id: channelId });
    }
  }, []);

  // DM functions
  const joinDm = useCallback((dmId) => {
    if (!dmId) return;
    joinedDmsRef.current.add(dmId);
    if (socketRef.current?.connected && authenticatedRef.current) {
      socketRef.current.emit('join_dm', { dm_id: dmId });
    }
  }, []);

  const leaveDm = useCallback((dmId) => {
    if (!dmId) return;
    joinedDmsRef.current.delete(dmId);
  }, []);

  // Typing functions.
  // The `username` argument is accepted and ignored: callers still pass it, but
  // the server reads the name off the authenticated session instead of trusting
  // the payload — otherwise anyone could appear to be typing under another
  // person's name. Kept in the signature so call sites need no change.
  const emitTypingStart = useCallback((channelId, _username) => {
    if (socketRef.current?.connected) {
      socketRef.current.emit('typing_start', { channel_id: channelId });
    }
  }, []);

  const emitTypingStop = useCallback((channelId) => {
    if (socketRef.current?.connected) {
      socketRef.current.emit('typing_stop', { channel_id: channelId });
    }
  }, []);

  const emitDmTypingStart = useCallback((dmId, _username) => {
    if (socketRef.current?.connected) {
      socketRef.current.emit('typing_start', { dm_id: dmId });
    }
  }, []);

  const emitDmTypingStop = useCallback((dmId) => {
    if (socketRef.current?.connected) {
      socketRef.current.emit('typing_stop', { dm_id: dmId });
    }
  }, []);

  // Mark messages seen
  const markMessagesSeen = useCallback((channelId) => {
    if (socketRef.current?.connected) {
      socketRef.current.emit('mark_messages_seen', { channel_id: channelId });
    }
  }, []);

  const getSocket = useCallback(() => socketRef.current, []);

  // ── Event listener factories ──────────────────────────────────────────────

  const onNewMessage = useCallback((cb) => {
    return subscribeToSocketEvent('new_message', cb);
  }, [subscribeToSocketEvent]);

  const onMessageEdited = useCallback((cb) => {
    return subscribeToSocketEvent('message_edited', cb);
  }, [subscribeToSocketEvent]);

  const onMessageDeleted = useCallback((cb) => {
    return subscribeToSocketEvent('message_deleted', cb);
  }, [subscribeToSocketEvent]);

  const onReactionUpdated = useCallback((cb) => {
    return subscribeToSocketEvent('reaction_updated', cb);
  }, [subscribeToSocketEvent]);

  const onMessageRead = useCallback((cb) => {
    return subscribeToSocketEvent('message_read', cb);
  }, [subscribeToSocketEvent]);

  const onMessagesSeen = useCallback((cb) => {
    return subscribeToSocketEvent('messages_seen', cb);
  }, [subscribeToSocketEvent]);

  const onMessagesRead = useCallback((cb) => {
    return subscribeToSocketEvent('messages_read', cb);
  }, [subscribeToSocketEvent]);

  const onChannelDeleted = useCallback((cb) => {
    return subscribeToSocketEvent('channel_deleted', cb);
  }, [subscribeToSocketEvent]);

  const onNewDmMessage = useCallback((cb) => {
    return subscribeToSocketEvent('new_dm_message', cb);
  }, [subscribeToSocketEvent]);

  const onDmMessageEdited = useCallback((cb) => {
    return subscribeToSocketEvent('dm_message_edited', cb);
  }, [subscribeToSocketEvent]);

  const onDmMessageDeleted = useCallback((cb) => {
    return subscribeToSocketEvent('dm_message_deleted', cb);
  }, [subscribeToSocketEvent]);

  const onDmReactionUpdated = useCallback((cb) => {
    return subscribeToSocketEvent('dm_reaction_updated', cb);
  }, [subscribeToSocketEvent]);

  const onDmMessagesSeen = useCallback((cb) => {
    return subscribeToSocketEvent('dm_messages_seen', cb);
  }, [subscribeToSocketEvent]);

  const onDmMessagesDelivered = useCallback((cb) => {
    return subscribeToSocketEvent('dm_messages_delivered', cb);
  }, [subscribeToSocketEvent]);

  const onDmUnreadUpdate = useCallback((cb) => {
    return subscribeToSocketEvent('dm_unread_update', cb);
  }, [subscribeToSocketEvent]);

  const onDmCreated = useCallback((cb) => {
    return subscribeToSocketEvent('dm_created', cb);
  }, [subscribeToSocketEvent]);

  const onUserOnline = useCallback((cb) => {
    return subscribeToSocketEvent('user_online', cb);
  }, [subscribeToSocketEvent]);

  const onUserOffline = useCallback((cb) => {
    return subscribeToSocketEvent('user_offline', cb);
  }, [subscribeToSocketEvent]);

  const onUserProfileUpdated = useCallback((cb) => {
    return subscribeToSocketEvent('user_profile_updated', cb);
  }, [subscribeToSocketEvent]);

  // Every member is stable for the provider's lifetime, so this object is created
  // once and never invalidates a consumer.
  const actionsValue = useMemo(() => ({
    getSocket,
    requestPresenceSnapshot,
    mergePresenceEntries,
    setTotalUnreadCount,
    joinChannel,
    leaveChannel,
    joinDm,
    leaveDm,
    emitTypingStart,
    emitTypingStop,
    emitDmTypingStart,
    emitDmTypingStop,
    markMessagesSeen,
    onNewMessage,
    onMessageEdited,
    onMessageDeleted,
    onReactionUpdated,
    onMessageRead,
    onMessagesSeen,
    onMessagesRead,
    onChannelDeleted,
    onNewDmMessage,
    onDmMessageEdited,
    onDmMessageDeleted,
    onDmReactionUpdated,
    onDmMessagesSeen,
    onDmMessagesDelivered,
    onDmUnreadUpdate,
    onDmCreated,
    onUserOnline,
    onUserOffline,
    onUserProfileUpdated,
  }), [
    getSocket,
    requestPresenceSnapshot,
    mergePresenceEntries,
    joinChannel,
    leaveChannel,
    joinDm,
    leaveDm,
    emitTypingStart,
    emitTypingStop,
    emitDmTypingStart,
    emitDmTypingStop,
    markMessagesSeen,
    onNewMessage,
    onMessageEdited,
    onMessageDeleted,
    onReactionUpdated,
    onMessageRead,
    onMessagesSeen,
    onMessagesRead,
    onChannelDeleted,
    onNewDmMessage,
    onDmMessageEdited,
    onDmMessageDeleted,
    onDmReactionUpdated,
    onDmMessagesSeen,
    onDmMessagesDelivered,
    onDmUnreadUpdate,
    onDmCreated,
    onUserOnline,
    onUserOffline,
    onUserProfileUpdated,
  ]);

  const statusValue = useMemo(() => ({ connected: connected && networkOnline, authenticated: authenticated && networkOnline }), [connected, authenticated, networkOnline]);
  const presenceValue = useMemo(
    () => ({ presenceByUser, presenceNow, onlineUsers }),
    [presenceByUser, presenceNow, onlineUsers]
  );
  const typingValue = useMemo(() => ({ typingUsers, dmTypingUsers }), [typingUsers, dmTypingUsers]);
  const unreadValue = useMemo(() => ({ totalUnreadCount }), [totalUnreadCount]);

  return (
    <SocketActionsContext.Provider value={actionsValue}>
      <SocketStatusContext.Provider value={statusValue}>
        <SocketPresenceContext.Provider value={presenceValue}>
          <SocketTypingContext.Provider value={typingValue}>
            <SocketUnreadContext.Provider value={unreadValue}>
              {children}
            </SocketUnreadContext.Provider>
          </SocketTypingContext.Provider>
        </SocketPresenceContext.Provider>
      </SocketStatusContext.Provider>
    </SocketActionsContext.Provider>
  );
};
